import React, { useEffect, useRef, useState } from "react";
import "../admin.css";
import {
  deleteGalleryImage,
  loadGalleryEntries,
  setGalleryStartImage,
  uploadGalleryFiles,
} from "../admin/gallery.js";

const DEFAULT_CROP = {
  offsetX: 0,
  offsetY: 0,
  zoom: 1,
};

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function replaceFileExtension(fileName, extension) {
  return String(fileName || "cropped-photo")
    .replace(/\.[^/.]+$/, "")
    .concat(extension);
}

function loadImage(previewUrl) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = previewUrl;
  });
}

function getSourceCrop({ imageHeight, imageWidth, offsetX, offsetY, targetHeight, targetWidth, zoom }) {
  const targetAspect = targetWidth / targetHeight;
  const imageAspect = imageWidth / imageHeight;
  let baseWidth = imageWidth;
  let baseHeight = imageHeight;

  if (imageAspect > targetAspect) {
    baseWidth = imageHeight * targetAspect;
  } else {
    baseHeight = imageWidth / targetAspect;
  }

  const safeZoom = clamp(Number(zoom) || 1, 1, 3);
  const cropWidth = baseWidth / safeZoom;
  const cropHeight = baseHeight / safeZoom;
  const maxOffsetX = Math.max((imageWidth - cropWidth) / 2, 0);
  const maxOffsetY = Math.max((imageHeight - cropHeight) / 2, 0);
  const centerX = imageWidth / 2 + (clamp(Number(offsetX) || 0, -100, 100) / 100) * maxOffsetX;
  const centerY = imageHeight / 2 + (clamp(Number(offsetY) || 0, -100, 100) / 100) * maxOffsetY;

  return {
    height: cropHeight,
    width: cropWidth,
    x: clamp(centerX - cropWidth / 2, 0, imageWidth - cropWidth),
    y: clamp(centerY - cropHeight / 2, 0, imageHeight - cropHeight),
  };
}

async function cropFileToSize(cropItem, { targetHeight, targetWidth }) {
  const image = await loadImage(cropItem.previewUrl);
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (!context) {
    throw new Error("This browser could not prepare the photo crop.");
  }

  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const sourceCrop = getSourceCrop({
    imageHeight: image.naturalHeight,
    imageWidth: image.naturalWidth,
    offsetX: cropItem.crop.offsetX,
    offsetY: cropItem.crop.offsetY,
    targetHeight,
    targetWidth,
    zoom: cropItem.crop.zoom,
  });

  context.drawImage(
    image,
    sourceCrop.x,
    sourceCrop.y,
    sourceCrop.width,
    sourceCrop.height,
    0,
    0,
    targetWidth,
    targetHeight
  );

  const blob = await new Promise((resolve) => {
    canvas.toBlob(resolve, "image/jpeg", 0.95);
  });

  if (!blob) {
    throw new Error("This browser could not export the cropped photo.");
  }

  return new File([blob], replaceFileExtension(cropItem.file.name, ".jpg"), {
    lastModified: Date.now(),
    type: "image/jpeg",
  });
}

function AdminGalleryManager({
  cropHeight = 1200,
  cropWidth = 1920,
  description,
  documentPath,
  enableCrop = true,
  emptyMessage,
  heading,
  multiple = true,
  replaceExisting = false,
  selectLabel = "Select Photos",
  storageDirectory,
  uploadCaution =
    "Caution: upload photos in the right dimensions or they may not fit correctly. Property and unit slides look best at a 16:10 ratio, such as 1920x1200.",
  uploadButtonLabel = "Upload Photos",
}) {
  const fileInputRef = useRef(null);
  const previousFilesRef = useRef([]);
  const [activeCropIndex, setActiveCropIndex] = useState(0);
  const [entries, setEntries] = useState([]);
  const [error, setError] = useState("");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [uploading, setUploading] = useState(false);
  const [deletingPath, setDeletingPath] = useState("");
  const [settingStartPath, setSettingStartPath] = useState("");

  useEffect(() => {
    let isMounted = true;

    async function loadEntries() {
      try {
        setLoading(true);
        setError("");
        const nextEntries = await loadGalleryEntries(documentPath);

        if (isMounted) {
          setEntries(nextEntries);
        }
      } catch (loadError) {
        if (isMounted) {
          setError(loadError.message || "We could not load these gallery images yet.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadEntries();

    return () => {
      isMounted = false;
    };
  }, [documentPath]);

  useEffect(() => {
    previousFilesRef.current.forEach((fileItem) => {
      if (fileItem.previewUrl && !files.some((nextFile) => nextFile.previewUrl === fileItem.previewUrl)) {
        URL.revokeObjectURL(fileItem.previewUrl);
      }
    });

    previousFilesRef.current = files;
  }, [files]);

  useEffect(() => {
    return () => {
      previousFilesRef.current.forEach((fileItem) => {
        if (fileItem.previewUrl) {
          URL.revokeObjectURL(fileItem.previewUrl);
        }
      });
    };
  }, []);

  function createFileItems(nextFiles) {
    return nextFiles.map((file, index) => ({
      crop: { ...DEFAULT_CROP },
      file,
      id: `${file.name}-${file.lastModified}-${index}`,
      previewUrl: URL.createObjectURL(file),
    }));
  }

  function handleFileChange(event) {
    const nextFiles = Array.from(event.target.files || []);
    setFiles(createFileItems(multiple ? nextFiles : nextFiles.slice(0, 1)));
    setActiveCropIndex(0);
    setStatus("");
    setError("");
  }

  function updateActiveCrop(cropKey, value) {
    setFiles((currentFiles) =>
      currentFiles.map((fileItem, index) =>
        index === activeCropIndex
          ? {
              ...fileItem,
              crop: {
                ...fileItem.crop,
                [cropKey]: Number(value),
              },
            }
          : fileItem
      )
    );
  }

  function resetActiveCrop() {
    setFiles((currentFiles) =>
      currentFiles.map((fileItem, index) =>
        index === activeCropIndex
          ? {
              ...fileItem,
              crop: { ...DEFAULT_CROP },
            }
          : fileItem
      )
    );
  }

  function clearSelectedFiles() {
    setFiles([]);
    setActiveCropIndex(0);
    setStatus("");
    setError("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  async function uploadSelectedFiles() {
    if (!files.length) {
      setError("Choose at least one photo before uploading.");
      return;
    }

    try {
      setUploading(true);
      setError("");
      setStatus("");

      const filesToUpload =
        enableCrop
          ? await Promise.all(
              files.map((fileItem) =>
                cropFileToSize(fileItem, {
                  targetHeight: cropHeight,
                  targetWidth: cropWidth,
                })
              )
            )
          : files.map((fileItem) => fileItem.file);

      const nextEntries = await uploadGalleryFiles({
        documentPath,
        files: filesToUpload,
        replaceExisting,
        storageDirectory,
      });

      setEntries(nextEntries);
      setFiles([]);
      setStatus(
        replaceExisting
          ? "Photo updated."
          : `${nextEntries.length ? "Gallery updated." : "Upload finished."}`
      );

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (uploadError) {
      setError(uploadError.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  }

  async function handleUpload(event) {
    event.preventDefault();
    await uploadSelectedFiles();
  }

  async function handleDelete(imagePath) {
    const shouldDelete = window.confirm("Delete this photo from Firebase and remove it from the gallery?");

    if (!shouldDelete) {
      return;
    }

    try {
      setDeletingPath(imagePath);
      setError("");
      setStatus("");

      const nextEntries = await deleteGalleryImage({
        documentPath,
        imagePath,
      });

      setEntries(nextEntries);
      setStatus("Photo deleted.");
    } catch (deleteError) {
      setError(deleteError.message || "Delete failed. Please try again.");
    } finally {
      setDeletingPath("");
    }
  }

  async function handleSetStart(entry) {
    try {
      setSettingStartPath(entry.path);
      setError("");
      setStatus("");

      const nextEntries = await setGalleryStartImage({
        documentPath,
        imagePath: entry.path,
      });

      setEntries(nextEntries);
      setStatus(`${entry.name} will now start this slideshow.`);
    } catch (startError) {
      setError(startError.message || "We could not update the first slideshow photo yet.");
    } finally {
      setSettingStartPath("");
    }
  }

  const activeFile = files[activeCropIndex] || files[0] || null;

  return (
    <section className="admin-card">
      <div className="admin-card__header">
        <div>
          <h2>{heading}</h2>
          {description ? <p>{description}</p> : null}
        </div>
        <div className="admin-card__meta">
          <span className="admin-badge">Firestore: {documentPath}</span>
          <span className="admin-badge">Storage: {storageDirectory}</span>
        </div>
      </div>

      <form className="admin-upload-form" onSubmit={handleUpload}>
        <label className="admin-upload-form__field">
          <span>{selectLabel}</span>
          <input
            accept="image/*"
            multiple={multiple}
            onChange={handleFileChange}
            ref={fileInputRef}
            type="file"
          />
        </label>
        <button className="btn btn-primary btn-lg" disabled={uploading} type="submit">
          {uploading ? (enableCrop ? "Cropping..." : "Uploading...") : uploadButtonLabel}
        </button>
        {files.length ? (
          <button
            className="btn btn-outline-dark btn-lg"
            disabled={uploading}
            onClick={clearSelectedFiles}
            type="button"
          >
            Cancel
          </button>
        ) : null}
      </form>

      {uploadCaution ? <p className="admin-upload-caution">{uploadCaution}</p> : null}

      {files.length ? (
        <p className="admin-inline-note">
          {files.length} file{files.length === 1 ? "" : "s"} ready to upload.
          {enableCrop ? ` Crop preview exports at ${cropWidth}x${cropHeight}.` : ""}
        </p>
      ) : null}

      {enableCrop && activeFile ? (
        <div className="admin-cropper">
          <div className="admin-cropper__header">
            <div>
              <p className="admin-page__eyebrow">Crop Preview</p>
              <h3>Frame Photo at {cropWidth} x {cropHeight}</h3>
            </div>
            <button className="btn btn-outline-dark" onClick={resetActiveCrop} type="button">
              Reset Crop
            </button>
          </div>

          {files.length > 1 ? (
            <div className="admin-cropper__file-list" aria-label="Selected photos">
              {files.map((fileItem, index) => (
                <button
                  className={`admin-cropper__file-button ${
                    index === activeCropIndex ? "is-active" : ""
                  }`.trim()}
                  key={fileItem.id}
                  onClick={() => setActiveCropIndex(index)}
                  type="button"
                >
                  Photo {index + 1}
                </button>
              ))}
            </div>
          ) : null}

          <div className="admin-cropper__frame" aria-label={`Crop preview for ${activeFile.file.name}`}>
            <img
              alt={`Crop preview for ${activeFile.file.name}`}
              src={activeFile.previewUrl}
              style={{
                objectPosition: `${50 + activeFile.crop.offsetX / 2}% ${
                  50 + activeFile.crop.offsetY / 2
                }%`,
                transform: `scale(${activeFile.crop.zoom})`,
              }}
            />
            <span className="admin-cropper__badge">{cropWidth} x {cropHeight}</span>
          </div>

          <div className="admin-cropper__controls">
            <label className="admin-cropper__control">
              <span>Zoom</span>
              <input
                max="3"
                min="1"
                onChange={(event) => updateActiveCrop("zoom", event.target.value)}
                step="0.01"
                type="range"
                value={activeFile.crop.zoom}
              />
            </label>

            <label className="admin-cropper__control">
              <span>Move Left / Right</span>
              <input
                max="100"
                min="-100"
                onChange={(event) => updateActiveCrop("offsetX", event.target.value)}
                step="1"
                type="range"
                value={activeFile.crop.offsetX}
              />
            </label>

            <label className="admin-cropper__control">
              <span>Move Up / Down</span>
              <input
                max="100"
                min="-100"
                onChange={(event) => updateActiveCrop("offsetY", event.target.value)}
                step="1"
                type="range"
                value={activeFile.crop.offsetY}
              />
            </label>
          </div>

          <div className="admin-cropper__actions">
            <button
              className="btn btn-primary btn-lg"
              disabled={uploading}
              onClick={uploadSelectedFiles}
              type="button"
            >
              {uploading ? "Cropping..." : uploadButtonLabel}
            </button>
            <button
              className="btn btn-outline-dark btn-lg"
              disabled={uploading}
              onClick={clearSelectedFiles}
              type="button"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {status ? <p className="admin-feedback admin-feedback--success">{status}</p> : null}
      {error ? <p className="admin-feedback admin-feedback--error">{error}</p> : null}

      {loading ? (
        <div className="admin-gallery-empty">Loading current photos...</div>
      ) : entries.length ? (
        <div className="admin-gallery-grid">
          {entries.map((entry, index) => (
            <article className="admin-gallery-card" key={entry.path}>
              <img alt={entry.name} className="admin-gallery-card__image" src={entry.url} />
              <div className="admin-gallery-card__body">
                <p className="admin-gallery-card__name">{entry.name}</p>
                <p className="admin-gallery-card__path">{entry.path}</p>
                {entries.length > 1 ? (
                  <button
                    className="admin-start-button"
                    disabled={
                      index === 0 ||
                      settingStartPath === entry.path ||
                      deletingPath === entry.path
                    }
                    onClick={() => handleSetStart(entry)}
                    type="button"
                  >
                    {index === 0
                      ? "Starts Slideshow"
                      : settingStartPath === entry.path
                        ? "Saving..."
                        : "Make First Slide"}
                  </button>
                ) : null}
                <button
                  className="admin-delete-button"
                  disabled={deletingPath === entry.path}
                  onClick={() => handleDelete(entry.path)}
                  type="button"
                >
                  {deletingPath === entry.path ? "Deleting..." : "Delete Photo"}
                </button>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="admin-gallery-empty">{emptyMessage || "No photos uploaded yet."}</div>
      )}
    </section>
  );
}

export default AdminGalleryManager;
